import type { GlanceMap } from '../schema/index.js';
import type { CacheResponse } from './cache-endpoint.js';
import type { ClientAdapter } from './adapter.js';
import {
  clampGlanceViewModel,
  errorView,
  rateLimitedView,
  type GlanceViewModel,
} from './render.js';

/**
 * H1 → H3 BRIDGE — assemble a `GlanceViewModel` from a cache envelope.
 *
 * This is the single, shared mapping from "what the cache returned" (H1
 * `CacheResponse`) to "what the renderer draws" (H3 `GlanceViewModel`), so
 * platform-dev and tile-dev never diverge on how an ok/stale/error/rateLimited
 * envelope becomes a tile. The data-bearing states (`ok`, `stale`) delegate to
 * the adapter's own `glanceView` (which knows how to format that tile's metrics
 * into the three slots); the non-data states use the shared factories.
 *
 * Pure & total: every envelope state maps to a view-model; it never throws. The
 * `dataState` from the envelope is preserved (so a `stale` envelope renders with
 * the stale freshness chrome even though its slots came from `glanceView`).
 *
 * GUARANTEE (code-review MEDIUM, 2026-07-24): the returned view-model ALWAYS
 * conforms to GLANCE_LIMITS. The data-bearing path runs the adapter's `glanceView`
 * output through `clampGlanceViewModel` so a sloppy adapter can never hand the
 * renderer an over-limit primary/delta/label; the non-data paths use the
 * state-view factories, which clamp their label too. Under-limit output is
 * unchanged (byte-identical). This makes the cap a contract, not a convention.
 *
 * @param res     the H1 response envelope
 * @param adapter the client half of the tile's adapter (provides `glanceView`)
 * @param glance  the validated glance map from the tile config
 * @param label   the tile's display label (or null)
 */
export function viewModelFromResponse<Data>(
  res: CacheResponse<Data>,
  adapter: Pick<ClientAdapter<unknown, Data>, 'glanceView'>,
  glance: GlanceMap,
  label: string | null = null,
): GlanceViewModel {
  switch (res.state) {
    case 'ok':
    case 'stale': {
      // Delegate slot/semantic formatting to the adapter, then overlay the
      // envelope's freshness state + timestamp (a stale envelope keeps `stale`).
      // Clamp LAST so the caps hold no matter what the adapter produced, and so
      // the fallback label (which the adapter didn't cap) is clamped too.
      const base = adapter.glanceView(res.data, glance);
      return clampGlanceViewModel({
        ...base,
        dataState: res.state,
        label: base.label ?? label,
        fetchedAt: res.meta.fetchedAt,
      });
    }
    case 'error': {
      const lastFetchedAt = 'lastKnown' in res && res.lastKnown !== undefined
        ? res.meta.fetchedAt
        : null;
      return errorView(label, lastFetchedAt);
    }
    case 'rateLimited': {
      return rateLimitedView(label, res.error.retryAtMs, null);
    }
  }
}
